import { ProductListItem } from "@components/product-list/product-list-item";
import Icon from "@repo/ui/components/ui/icon/icon";
import { useState, useEffect } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@repo/ui/components/ui/carousel";
import { useMediaQuery } from "@repo/ui/lib/use-media-query-hook";
import { Product } from "@/types/product-search";
import { useTranslations, useFormatter } from "next-intl";
import { Category } from "@/types/homepage";
import NoResults from "@repo/next-shared/components/no-results";
import { useBase64QueryState } from "@repo/next-shared/utils/nuqs-custom-parsers";
import FiltersChips from "@components/filters-menu/filters-chips";
import FiltersSidebar from "@components/filters-sidebar/filters-sidebar";

export const ProductList = ({
  products,
  totalProducts,
  category,
  bestOf,
  ...props
}: {
  products: Product[];
  totalProducts?: number;
  category?: Category | null;
  bestOf?: Product[];
}) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [itemsPerRow, setItemsPerRow] = useState(isDesktop ? "less" : "more");
  const t = useTranslations("Products-list");
  const format = useFormatter();
  const [filters, setFilters] = useBase64QueryState(null, {
    history: "push",
    shallow: false,
  });

  const NON_FILTER_KEYS = new Set(["pageNumber", "pageSize"]);

  const isEmptyFilterValue = (value: unknown) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return Object.keys(value).length === 0;
    return false;
  };

  const hasActiveFilters =
    !!filters &&
    Object.entries(filters).some(
      ([key, value]) => !NON_FILTER_KEYS.has(key) && !isEmptyFilterValue(value),
    );

  useEffect(() => {
    if (filters && filters.pageSize) {
      setItemsPerRow(filters.pageSize === 72 ? "more" : "less");
    }
  }, [filters]);

  return (
    <div>
      <>
        {!!totalProducts && (
          <span className="text-[#333]/50 dark:text-primary-dustyWhite text-xl mb-5">
            {t("articles", { count: format.number(totalProducts) })}
          </span>
        )}

        <div className="py-4 mb-8 flex justify-between items-center max-md:-mx-[1.5rem] max-md:px-[1.5rem] max-md:sticky max-md:top-[85px] max-md:z-10 max-md:bg-primary-white max-md:dark:bg-primary-black">
          <FiltersSidebar />
          <div className="flex gap-4 justify-end items-center h-5 stroke-primary-black dark:stroke-primary-white">
            <div
              role="button"
              onClick={() => {
                // setFilters((prev: any) => {
                //   return {
                //     ...prev,
                //     pageSize: 30,
                //   };
                // });
                setItemsPerRow("less");
              }}
            >
              <Icon
                className={`w-[20px] h-[20px] 
            ${
              itemsPerRow === "less"
                ? "[&_svg]:stroke-primary-magenta dark:[&_svg]:stroke-primary-magenta"
                : ""
            }`}
                icon="Square"
              ></Icon>
            </div>
            <div
              role="button"
              onClick={() => {
                // setFilters((prev: any) => {
                //   return {
                //     ...prev,
                //     pageSize: 32,
                //   };
                // });
                setItemsPerRow("more");
              }}
            >
              <Icon
                className={`w-[20px] h-[20px] 
            ${
              itemsPerRow === "more"
                ? "[&_svg]:stroke-primary-magenta dark:[&_svg]:stroke-primary-magenta"
                : ""
            }`}
                icon="Squares"
              ></Icon>
            </div>
          </div>
        </div>
      </>

      <section className="max-md:hidden">
        <FiltersChips />
      </section>

      {products && products.length === 0 && (
        <NoResults className={"my-20 text-3xl font-semibold"} />
      )}
      <div
        className={`grid ${itemsPerRow === "less" ? "grid-cols-6 max-md:grid-cols-1" : "grid-cols-8 max-md:grid-cols-2"} gap-5 gap-y-16 max-md:gap-y-8 mb-10`}
        {...props}
      >
        {products &&
          products
            .slice(0, itemsPerRow === "less" ? 12 : 16)
            .map((product) => (
              <ProductListItem key={product.id} product={product} />
            ))}
      </div>
      {!hasActiveFilters && bestOf && bestOf.length > 0 && category && (
        <>
          <div className="mx-[-40px] bg-secondary-gray1 max-md:mx-[-24px] p-10 pb-21 max-md:p-6 max-md:pb-5 max-md:gap-y-8 dark:bg-secondary-black w-[100vw] max-w-[1920px]">
            <h4 className="text-[30px] w-full max-md:text-[20px] max-md:font-medium text-left mb-[40px] max-md:mb-[20px]">
              <span className="text-primary-magenta">{t("best-of")}</span>{" "}
              <span className="text-primary-black dark:text-primary-white">
                {" "}
                {category && category.title}
              </span>
            </h4>
            <Carousel
              scrollSnapPosition="start"
              scrollSnapClass="bottom-0 max-md:bottom-5"
              scrollSnapSlim
              autoplay={false}
              className="w-full rounded-b-none"
            >
              <CarouselContent className="pb-0">
                {bestOf.map((product, key) => (
                  <CarouselItem
                    key={key}
                    className={`w-full pb-10 ${itemsPerRow === "less" ? (isDesktop ? "basis-1/6" : "basis-1/1") : isDesktop ? "basis-[12.5%]" : "basis-1/2 pl-4 [&:not(:first-child)]:pl-5"}`}
                  >
                    <ProductListItem
                      key={product.id}
                      product={product}
                    ></ProductListItem>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </>
      )}
      <div
        className={`grid ${itemsPerRow === "less" ? "grid-cols-6 max-md:grid-cols-1" : "grid-cols-8 max-md:grid-cols-2"} gap-5 gap-y-10 max-md:gap-y-8 mt-10`}
        {...props}
      >
        {products &&
          products
            .slice(itemsPerRow === "less" ? 12 : 16, products.length)
            .map((product) => (
              <ProductListItem
                key={product.id}
                product={product}
              ></ProductListItem>
            ))}
      </div>
    </div>
  );
};
